import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatDateLongFR } from "../lib/utils";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GroupedPlanning = () => {
  const [planningData, setPlanningData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [expandedHousings, setExpandedHousings] = useState(new Set());
  const [filterDate, setFilterDate] = useState("");

  useEffect(() => {
    loadGroupedPlanning();
  }, [filterDate]);

  const loadGroupedPlanning = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDate) {
        params.start_date = filterDate;
        params.end_date = filterDate;
      }
      const response = await axios.get(`${API}/planning/grouped`, { params });
      setPlanningData(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement du planning groupé:", error);
      toast.info("Erreur lors du chargement du planning");
    } finally {
      setLoading(false);
    }
  };

  const toggleClient = (clientId) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedClients(newExpanded);
  };

  const toggleHousing = (housingId) => {
    const newExpanded = new Set(expandedHousings);
    if (newExpanded.has(housingId)) {
      newExpanded.delete(housingId);
    } else {
      newExpanded.add(housingId);
    }
    setExpandedHousings(newExpanded);
  };

  const expandAll = () => {
    if (planningData) {
      const allClients = new Set(planningData.clients.map(c => c.client.id));
      const allHousings = new Set();
      planningData.clients.forEach(c => {
        c.housings.forEach(h => allHousings.add(h.housing.id));
      });
      setExpandedClients(allClients);
      setExpandedHousings(allHousings);
    }
  };

  const collapseAll = () => {
    setExpandedClients(new Set());
    setExpandedHousings(new Set());
  };

  const getStatusColor = (status) => {
    const colors = {
      'planifie': 'bg-blue-100 text-blue-800 border-blue-300',
      'en_cours': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'termine': 'bg-green-100 text-green-800 border-green-300',
      'annule': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'planifie': 'Planifiée',
      'en_cours': 'En cours',
      'termine': 'Terminée',
      'annule': 'Annulée'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement du planning groupé...</span>
      </div>
    );
  }

  if (!planningData || planningData.clients.length === 0) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune donnée de planning
          </h3>
          <p className="text-gray-500 mb-4">
            Créez des clients, logements et missions pour voir le planning groupé
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4" data-testid="grouped-planning">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">📋 Planning par Client</h1>
            <p className="text-gray-600 mt-2">
              Vue hiérarchique : Client → Logements → Missions
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={expandAll}
              className="btn-secondary"
              data-testid="expand-all-btn"
            >
              ⬇️ Tout déplier
            </button>
            <button
              onClick={collapseAll}
              className="btn-secondary"
              data-testid="collapse-all-btn"
            >
              ⬆️ Tout replier
            </button>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filtrer par date
              </label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="form-input"
                data-testid="filter-date-input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilterDate("")}
                className="btn-secondary"
                data-testid="clear-date-filter-btn"
              >
                🧹 Effacer filtre
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-blue-600">
              {planningData.total_clients}
            </div>
            <div className="text-sm text-gray-600">Clients</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-purple-600">
              {planningData.total_housings}
            </div>
            <div className="text-sm text-gray-600">Logements</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-green-600">
              {planningData.total_missions}
            </div>
            <div className="text-sm text-gray-600">Missions</div>
          </div>
        </div>
      </div>

      {/* Planning groupé hiérarchique */}
      <div className="space-y-4">
        {planningData.clients.map((clientData) => {
          const isClientExpanded = expandedClients.has(clientData.client.id);
          const clientMissionsCount = clientData.housings.reduce(
            (sum, h) => sum + h.missions.length, 0
          );

          return (
            <div key={clientData.client.id} className="card">
              {/* Client Header */}
              <div
                className="card-header cursor-pointer hover:bg-gray-50"
                onClick={() => toggleClient(clientData.client.id)}
                data-testid={`client-header-${clientData.client.id}`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">
                      {isClientExpanded ? "📂" : "📁"}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        👤 {clientData.client.prenom} {clientData.client.nom}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {clientData.housings.length} logement(s) • {clientMissionsCount} mission(s)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <span className="text-sm">
                      📞 {clientData.client.telephone}
                    </span>
                    {clientData.client.email && (
                      <span className="text-sm">
                        ✉️ {clientData.client.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Logements du client */}
              {isClientExpanded && (
                <div className="card-body bg-gray-50">
                  <div className="space-y-3">
                    {clientData.housings.map((housingData) => {
                      const isHousingExpanded = expandedHousings.has(housingData.housing.id);
                      
                      return (
                        <div key={housingData.housing.id} className="bg-white rounded-lg border border-gray-200">
                          {/* Housing Header */}
                          <div
                            className="p-4 cursor-pointer hover:bg-gray-50"
                            onClick={() => toggleHousing(housingData.housing.id)}
                            data-testid={`housing-header-${housingData.housing.id}`}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center space-x-3">
                                <span className="text-lg">
                                  {isHousingExpanded ? "🏠" : "🏘️"}
                                </span>
                                <div>
                                  <h4 className="font-semibold text-gray-900">
                                    🎤 {housingData.housing.pseudo}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    📍 {housingData.housing.adresse}, {housingData.housing.ville} {housingData.housing.code_postal}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {housingData.housing.type_logement} • {housingData.housing.duree_estimee} min
                                  </p>
                                </div>
                              </div>
                              <div className="text-sm font-medium text-blue-600">
                                {housingData.missions.length} mission(s)
                              </div>
                            </div>
                          </div>

                          {/* Missions du logement */}
                          {isHousingExpanded && (
                            <div className="px-4 pb-4">
                              {housingData.missions.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                  Aucune mission pour ce logement
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {housingData.missions.map((missionData) => {
                                    const mission = missionData.mission;
                                    const employee = missionData.employee;
                                    
                                    return (
                                      <div
                                        key={mission.id}
                                        className={`p-3 rounded-lg border ${getStatusColor(mission.status)}`}
                                        data-testid={`mission-${mission.id}`}
                                      >
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                              <span className="font-medium">
                                                📅 {formatDateLongFR(new Date(mission.date_mission))}
                                              </span>
                                              <span className="text-sm">
                                                🕐 {mission.heure_debut} - {mission.heure_fin}
                                              </span>
                                            </div>
                                            <div className="text-sm">
                                              {employee ? (
                                                <span className="text-green-700 font-medium">
                                                  👷 {employee.prenom} {employee.nom}
                                                </span>
                                              ) : (
                                                <span className="text-red-600 font-medium">
                                                  ⚠️ Non assignée
                                                </span>
                                              )}
                                            </div>
                                            {mission.notes && (
                                              <div className="text-xs text-gray-700 mt-1">
                                                📝 {mission.notes}
                                              </div>
                                            )}
                                          </div>
                                          <div className="text-right">
                                            <div className="text-xs font-medium px-2 py-1 rounded">
                                              {getStatusLabel(mission.status)}
                                            </div>
                                            {mission.prix && (
                                              <div className="text-sm font-bold text-green-700 mt-1">
                                                {mission.prix}€
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupedPlanning;
